import * as actions from './actions';
import type { CalculationState } from './createCalculationState';
import { createCalculationState } from './createCalculationState';
import type { EvaluatedGridElementType } from './derived-state/gridElementsDetailsByCalculationType/GridElementType';
import type { GridElementLoadFlowDetails } from './derived-state/gridElementsDetailsByCalculationType/loadFlow/gridElementLoadFlowDetails.types';
import type { GridElementShortCircuitDetails } from './derived-state/gridElementsDetailsByCalculationType/shortCircuit/ShortCircuitDetails';
import type {
  AcLineSegment,
  Busbar,
  EnergyConsumer,
  ExternalNetworkInjection,
  FunctionalGrid,
  FunctionalGridElementTypes
} from './grid/FunctionalGrid';

export type EvaluatedStatus = 'Good' | 'Warning' | 'Bad' | 'NotEvaluated';
export type TacticResultItem =
  | AcLineSegment
  | EnergyConsumer
  | Busbar
  | ExternalNetworkInjection;

export type Calculation = {
  tacticResult: {
    acLineSegments: AcLineSegment[];
    energyConsumers: EnergyConsumer[];
    busBars: Busbar[];
    externalNetworkInjections: ExternalNetworkInjection[];
  };
};

export type NonEmptyArray<T> = [T, ...T[]];

export type CalculationTacticExecutionId = string;
export type CalculationTacticId = string;
export type CalculationTacticName = string;
export type VoltageLevel = 'LowVoltage' | 'MediumVoltage';

export type GridElementIdentity = {
  id: DomainGuid;
  objectType: FunctionalGridElementTypes;
};

export type GridElementTypes = // TODO: define difference in name between this type and FunctionalGridElementTypes - Types that make sense to display results - no trafo ends, no terminals, no switches

    | 'AcLineSegment'
    | 'Busbar'
    | 'EnergyConsumer'
    | 'ExternalNetworkInjection'
    | 'PowerTransformer';

export type ShortCircuitScenarioElementCalculation = {
  result: ShortCircuitElementResults;
} & GridElementIdentity;

export type ShortCircuitElementResults = ShortCircuitCurrentResults &
  ShortCircuitProtectiveResults;
export type CurrentValue = number;
export type ShortCircuitCurrentResults = {
  currentPhase1Min: CurrentValue;
  currentPhase2Min: CurrentValue;
  currentPhase3Min: CurrentValue;
  currentPhase1Max: CurrentValue;
  currentPhase2Max: CurrentValue;
  currentPhase3Max: CurrentValue;
};
export type ShortCircuitProtectiveResults = {
  releaseTime: ReleaseTimeType;
};

export type ReleaseTimeType = {
  result?: ReleaseTimeResult;
  message?: string;
  isSuccess: boolean;
};

export type ReleaseTimeResult = {
  value?: number;
  unit?: string;
};

export type ShortCircuitScenarioResults = {
  scenarioId: string;
  status: CalculationTacticResultScenarioStatus;
  scenarioName: string;
  results: ShortCircuitScenarioElementCalculation[];
  inputs: ShortCircuitScenarioInputs;
};

export type PowerTransformerEndObject = GridElementIdentity & {
  objectType: 'PowerTransformerEnd';
  result: {
    voltage: number;
  };
};

export type LoadFlowScenarioElementCalculation =
  | BusbarObject
  | AcLineSegmentObject
  | PowerTransformerObject
  | PowerTransformerEndObject
  | EnergyConsumerObject;

export type LoadFlowMergedScenariosResult = LoadFlowScenarioElementCalculation;

export type PowerTransformerObject = GridElementIdentity & {
  objectType: 'PowerTransformer';
  result: PowerTransformerResult;
};

export type PowerTransformerResult = {
  loadingPercent: number;
  loading: number;
  availableCapacity: number | null;
};

export type EnergyConsumerObject = GridElementIdentity & {
  objectType: 'EnergyConsumer';
  result: EnergyConsumerResult;
  input: {
    q: number;
    p: number;
  };
};

export type EnergyConsumerResult = {
  voltage: number;
  voltageDiffNominal: number;
  voltageDiffTransformerEnd: number;
  voltageDiffTransformerEndPercent: number;
  voltageCosinePhi: number;
};

export type AcLineSegmentObject = GridElementIdentity & {
  objectType: 'AcLineSegment';
  result: AcLineSegmentResult;
};

export type BusbarObject = GridElementIdentity & {
  objectType: 'Busbar';
  result: BusbarResult;
};

export type BusbarResult = {
  voltage: number;
  voltageDiffNominal: number;
  voltageDiffTransformerEnd: number;
  voltageDiffTransformerEndPercent: number;
  voltageCosinePhi: number;
};

export type AcLineSegmentResult = {
  current: number;
  loadingPercent: number;
  availableCapacity: number;
};

export type CalculationTacticResultScenarioStatus = 'Completed' | 'Failed';
export type LoadFlowScenarioLoadInput = {
  p: number;
  q: number;
  id: string;
  loadSource?: string;
};

export type LoadFlowScenarioInputs = {
  loads: LoadFlowScenarioLoadInput[];
  startValues: LoadFlowScenarioStartValue[];
};

export type ShortCircuitScenarioInputs = {
  startValues: ShortCircuitScenarioStartValue[];
};

export type ShortCircuitScenarioStartValue = {
  id: string;
  connectedToId: DomainGuid;
  minInitialSymShCCurrent: number;
  maxInitialSymShCCurrent: number;
  powerFactorMin: number;
  powerFactorMax: number;
};

export type LoadFlowScenarioResults = {
  status: CalculationTacticResultScenarioStatus;
  scenarioName: string;
  scenarioId: string;
  results: LoadFlowScenarioElementCalculation[];
  inputs: LoadFlowScenarioInputs;
};

export type LoadFlowScenarioStartValue = {
  id: string;
  connectedToId: DomainGuid;
  voltage: number;
};

export type CalculationScenarios = {
  shortCircuit: ShortCircuitScenarioResults[];
  loadFlow: LoadFlowScenarioResults[];
};

export type ContainmentGrid = Container[];

export type Container = {
  containerId: DomainGuid;
  containerType: 'Cabinet' | 'Substation';
  containedObjects: { objectId: DomainGuid }[];
};

export type ContainmentResult = {
  containment: ContainmentGrid;
};

export type ContainmentResponse = {
  containment: ContainmentGrid;
  executionTime: TimeInterval;
};

export type CalculationTacticGridModels = {
  functional: FunctionalGrid;
  containment: ContainmentGrid;
};

export type TimeInterval = {
  startTime: string;
  endTime: string;
};
export type CalculationTacticStepsExecutionTimes = {
  calculations: TimeInterval;
  gettingTopology: TimeInterval;
  gettingContainment: TimeInterval;
};

export type EnrichedCompletedCalculationTactic = CompletedCalculationTactic;
export type CompletedCalculationTactic = {
  calculationSetExecutionId: CalculationTacticExecutionId;
  status: 'Completed';
  calculationSetResult: {
    calculationSetId: CalculationTacticId;
    calculationSetName: CalculationTacticName;
    voltageLevel: VoltageLevel;
    calculationScenarios: CalculationScenarios;
    grid: CalculationTacticGridModels;
    executionTimes: CalculationTacticStepsExecutionTimes;
  };
};

export type FailedCalculationTactic = {
  calculationSetExecutionId: CalculationTacticExecutionId;
  status: 'Failed';
  details: string;
};

export type CalculationTactic =
  | CompletedCalculationTactic
  | FailedCalculationTactic;
/* eslint-enable @typescript-eslint/naming-convention */

export type DomainGuid = `{${string}}`;

export type GridElementSection<TGridElement extends GridElement> = {
  type: string;
  header: string;
  gridElements: TGridElement[];
};

export type GridElementSectionWithQuickAccessResult =
  GridElementSection<GridElementWithQuickAccessResult>;

export type QuickAccessEvaluatedResult = {
  label: string;
  value: string;
};

export type GridElementQuickAccessEvaluatedResult =
  | {
      type: 'EVALUATED_RESULT_FOR_QUICK_ACCESS';
      evaluatedResults: QuickAccessEvaluatedResult[];
    }
  | { type: 'NO_EVALUATED_RESULTS_FOR_QUICK_ACCESS' };

export type GridElement = {
  objectId: `{${string}}`;
  label: string;
  title: string;
};

export type GridElementWithQuickAccessResult = GridElement & {
  evaluatedResults: GridElementQuickAccessEvaluatedResult;
};

export type RawData =
  | { type: 'NO_DETAILS' }
  | { type: 'GRID_ELEMENT_DETAILS'; details: SelectedGridElementDetails };

export type SelectedGridElementDetails = Record<string, string>; // ArcGis?
type SelectedGridElementLabel = {
  name: string;
  title: string;
};

export type ScenarioName = string;
export type SelectedGridElementLoadFlowDetails<
  TGridElementType extends EvaluatedGridElementType
> =
  | {
      type: 'NO_LOAD_FLOW_DETAILS';
    }
  | {
      type: 'LOAD_FLOW_DETAILS';
      details: LoadFlowDetails<TGridElementType>;
    }
  | {
      type: 'ALL_LOAD_FLOW_SCENARIO_DETAILS';
      details: {
        scenarioName: ScenarioName;
        scenarioDetails: LoadFlowDetails<TGridElementType>;
      }[];
    };

export type SelectedGridElement<
  TGridElementType extends EvaluatedGridElementType
> = {
  objectId: DomainGuid;
  objectType: TGridElementType;
  shortCircuit:
    | { type: 'NO_SHORT_CIRCUIT_DETAILS' }
    | {
        type: 'SHORT_CIRCUIT_DETAILS';
        evaluatedResults: ShortCircuitDetails<TGridElementType>;
      };
  loadflow: SelectedGridElementLoadFlowDetails<TGridElementType>;
  label: SelectedGridElementLabel;
  attentionRequiringResultsCount: number;
};

export type LoadFlowDetails<TGridElementType> = Extract<
  GridElementLoadFlowDetails,
  { objectType: TGridElementType }
>;

export type ShortCircuitDetails<TGridElementType> = Extract<
  GridElementShortCircuitDetails,
  { objectType: TGridElementType }
>;

export type AvailableCalculationScenarios =
  | { type: 'SOME_AVAILABLE'; scenarios: Set<'LOAD_FLOW' | 'SHORT_CIRCUIT'> }
  | { type: 'ALL_AVAILABLE' } // scenario contains both LF and SC calculations. No matter success or failed
  | { type: 'NONE_AVAILABLE' };

export type CalculationScenarioAvailability<
  TCalculationType extends 'LOAD_FLOW' | 'SHORT_CIRCUIT'
> =
  | { type: 'NOT_AVAILABLE' }
  | { type: 'AVAILABLE'; calculationType: TCalculationType };

// TODO: Should it be just string
export type TacticResultBrowsingFilterToCountMapping = Record<string, number>;

const state: CalculationState = createCalculationState();

export { actions, state };
export type { CalculationState };

export type ShortCircuitFaultTypes =
  | 'Phase1Min'
  | 'Phase1Max'
  | 'Phase2Min'
  | 'Phase2Max'
  | 'Phase3Min'
  | 'Phase3Max';
